import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled,
  RedoOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { IdeyaButton } from '@/common/antd/ideya-button/ideya-button.tsx'
import IdeyaTypography from '@/common/antd/ideya-typography.tsx'
import { IdeaDirectionType } from '@/enums/idea-list.enum.ts'

const I18N_PREFIX = 'idea_list.idea_modal.action_idea_form'

const formatAmount = (value: number) =>
  Math.abs(Math.round(value)).toLocaleString('en-US')

const formatPercent = (value: number, base: number) =>
  base ? `${((Math.abs(value) / Math.abs(base)) * 100).toFixed(1)}%` : ''

// Short side: adding exposure is "sell short", reducing it is "buy to cover".
const getOrderKey = (
  orderAmount: number,
  direction: IdeaDirectionType | ''
) => {
  if (direction === IdeaDirectionType.Short) {
    return orderAmount > 0
      ? 'rebalance_sell_short_on_submit'
      : 'rebalance_cover_on_submit'
  }
  return orderAmount > 0
    ? 'rebalance_buy_on_submit'
    : 'rebalance_sell_on_submit'
}

interface RebalanceNoteProps {
  targetMarketValue: number
  currentMarketValue: number
  direction: IdeaDirectionType | ''
  isRebalanced: boolean
  onRebalance: () => void
  onReset: () => void
}

export const RebalanceNote = ({
  targetMarketValue,
  currentMarketValue,
  direction,
  isRebalanced,
  onRebalance,
  onReset,
}: RebalanceNoteProps) => {
  const { t } = useTranslation()

  const orderAmount = Math.round(targetMarketValue - currentMarketValue)

  if (orderAmount === 0) {
    return (
      <div className={'rebalance-note'}>
        <ExclamationCircleFilled
          className={'rebalance-note__icon rebalance-note__icon--warning'}
        />
        <IdeyaTypography.Text
          size={'sm'}
          textStyle={'strong'}
          className={'rebalance-note__text'}
        >
          {t(`${I18N_PREFIX}.rebalance_at_target`)}
        </IdeyaTypography.Text>
      </div>
    )
  }

  if (isRebalanced) {
    return (
      <div className={'rebalance-note'}>
        <CheckCircleFilled
          className={'rebalance-note__icon rebalance-note__icon--success'}
        />
        <IdeyaTypography.Text
          size={'sm'}
          textStyle={'strong'}
          className={'rebalance-note__text'}
        >
          {t(`${I18N_PREFIX}.${getOrderKey(orderAmount, direction)}`, {
            amount: `$${formatAmount(orderAmount)}`,
          })}
        </IdeyaTypography.Text>
        <IdeyaButton
          ideyaType={'bw'}
          size={'small'}
          icon={<RedoOutlined />}
          onClick={onReset}
        >
          {t('idea_list.idea_modal.action_idea_form.rebalance_reset')}
        </IdeyaButton>
      </div>
    )
  }

  // Gap = current MV − target; percent is relative to target.
  const positionGap = currentMarketValue - targetMarketValue
  return (
    <div className={'rebalance-note'}>
      <InfoCircleFilled
        className={'rebalance-note__icon rebalance-note__icon--info'}
      />
      <IdeyaTypography.Text size={'sm'} className={'rebalance-note__text'}>
        {t(
          `${I18N_PREFIX}.${positionGap > 0 ? 'rebalance_no_order_above' : 'rebalance_no_order_below'}`,
          {
            direction: direction
              ? t(`idea_list.idea_table.direction.${direction.toLowerCase()}`)
              : '',
            current: `$${formatAmount(currentMarketValue)}`,
            gap: `$${formatAmount(positionGap)}`,
            percent: formatPercent(positionGap, targetMarketValue),
          }
        )}
      </IdeyaTypography.Text>
      <IdeyaButton size={'small'} onClick={onRebalance}>
        {t(`${I18N_PREFIX}.rebalance`)}
      </IdeyaButton>
    </div>
  )
}
